import { Button } from '@design-system';
import { EditAssumptionsHeader, EditAssumptionsContent } from '@/features/edit-assumptions';

import { Check, Loader2 } from 'lucide-react';

import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { putClientByClientIdRequest, selectClient, selectClientUpdatesStatus } from '@/model';

interface EditAssumptionsPageProps {
  clientId: string;
}

const SAVE_STATUS_KEYS: ClientUpdateKeys[] = [
  'ltcAtAge',
  'phaseDurations',
  'healthCareInflationRate',
  'careEnvironmentSelections',
  'careEnvironmentCosts',
];

export function EditAssumptionsPage(_props: EditAssumptionsPageProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { clientId } = useParams<{ clientId: string }>();
  const [searchParams] = useSearchParams();
  const client = useSelector(selectClient);
  const clientUpdates = useSelector(selectClientUpdatesStatus);
  const isFromOverview = searchParams.get('from') === 'overview';

  const flushRef = useRef<(() => void) | null>(null);
  const isSaving = SAVE_STATUS_KEYS.some(key => clientUpdates[key] === 'loading');
  const hasError = SAVE_STATUS_KEYS.some(key => clientUpdates[key] === 'error');

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleDone = useCallback(() => {
    if (isSaving || hasError) return;

    flushRef.current?.();

    if (client.metadata?.assumptionsSet !== true) {
      dispatch(
        putClientByClientIdRequest({
          ...client,
          metadata: { ...client.metadata, assumptionsSet: true },
        }),
      );
    }

    navigate(`/clients/${clientId}/onboarding/overview`);
  }, [navigate, clientId, isSaving, hasError, client, dispatch]);

  return (
    <div className="bg-background flex min-h-screen flex-col">
      <EditAssumptionsHeader onBack={handleBack} />
      <div className="px-4">
        <div className="mx-auto w-full max-w-4xl flex-1 py-6 pb-32">
          <EditAssumptionsContent flushRef={flushRef} />
        </div>
      </div>
      {isFromOverview && (
        <div className="z-99 fixed bottom-0 left-0 right-0 w-full md:left-1/2 md:w-fit md:-translate-x-1/2">
          <div className="pr-24! md:pr-4! bg-background fixed bottom-0 left-0 right-0 flex w-full flex-row flex-wrap items-end gap-3 p-4 shadow-[0_-2px_4px_rgba(0,0,0,0.1)] md:relative md:bottom-5 md:my-4 md:flex-nowrap md:gap-4 md:rounded-full md:border-0 md:shadow-xl">
            <Button
              size="lg"
              onClick={handleDone}
              disabled={isSaving || hasError}
              className="max-h-16 rounded-full px-6 py-4 md:min-w-32 md:px-8 md:py-6 md:text-lg"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
